const VIDEO_URL_RE = /\.(mp4|webm|ogg|ogv|mov|m4v|avi|mkv)(\?|#|$)/i;

export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

/** Format a money amount with an ISO currency code (e.g. cad → CAD). */
export function formatMoney(amount, currencyCode = "USD") {
  const code = String(currencyCode || "USD")
    .trim()
    .toUpperCase();
  const value = Number(amount);
  const safe = Number.isFinite(value) ? value : 0;
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code.length === 3 ? code : "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(safe);
  } catch {
    return formatCurrency(safe);
  }
}

export function getInvestmentRecordId(inv) {
  return inv?._id ?? inv?.id ?? "";
}

export function getCampaignRouteId(inv) {
  return inv?.project?._id ?? inv?.project?.id ?? getInvestmentRecordId(inv);
}

export function getProjectTitle(inv) {
  return inv?.project?.title ?? inv?.project?.name ?? "Project";
}

export function humanizeStatus(status) {
  if (!status || typeof status !== "string") return "";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

/** Payment statuses for `/api/investments/me` (`query.status`). */
export const INVESTMENT_PAYMENT_STATUSES = ["pending", "paid", "failed", "cancelled"];

export const INVESTMENT_STATUS_TABS = [
  { value: "all", label: "All", status: "" },
  { value: "pending", label: "Pending", status: "pending" },
  { value: "paid", label: "Paid", status: "paid" },
  { value: "failed", label: "Failed", status: "failed" },
  { value: "cancelled", label: "Cancelled", status: "cancelled" },
];

export function getInvestmentPaymentStatus(inv) {
  return String(inv?.status ?? "").toLowerCase();
}

export function matchesInvestmentPaymentStatus(inv, status) {
  if (!status) return true;
  return getInvestmentPaymentStatus(inv) === status;
}

export function isPendingInvestment(inv) {
  return getInvestmentPaymentStatus(inv) === "pending";
}

export function isPaidInvestment(inv) {
  return getInvestmentPaymentStatus(inv) === "paid";
}

export function isFailedInvestment(inv) {
  return getInvestmentPaymentStatus(inv) === "failed";
}

export function isCancelledInvestment(inv) {
  return getInvestmentPaymentStatus(inv) === "cancelled";
}

/** @deprecated Use `isPendingInvestment` / payment status helpers. */
export function isActiveInvestment(inv) {
  return isPendingInvestment(inv) || isPaidInvestment(inv);
}

export function isFundedInvestment(inv) {
  if (inv?.project?.status === "funded") return true;
  return getInvestmentPaymentStatus(inv) === "paid";
}

/** Normalise list payloads from `/api/investments/me` and dashboard. */
export function parseInvestmentsListResponse(resp) {
  const root = resp?.data ?? resp ?? {};
  if (Array.isArray(root)) return root;
  if (Array.isArray(root.data)) return root.data;
  if (Array.isArray(root.investments)) return root.investments;
  if (Array.isArray(root.items)) return root.items;
  return [];
}

/** List + pagination fields from `/api/investments/me` (server-side). */
export function parseMyInvestmentsListResult(resp) {
  const root = resp?.data ?? resp ?? {};
  const meta = root.meta ?? root.pagination ?? {};
  const investments = parseInvestmentsListResponse(resp);
  const total = Number(
    resp?.total ?? root.total ?? root.totalCount ?? meta.total ?? meta.totalCount ?? 0
  );
  const limit = Number(resp?.limit ?? root.limit ?? meta.limit ?? 0);
  const page = Number(resp?.page ?? root.page ?? meta.page ?? 1);

  return {
    investments,
    total: Number.isFinite(total) ? total : investments.length,
    limit: Number.isFinite(limit) ? limit : 0,
    page: Number.isFinite(page) ? page : 1,
  };
}

function mediaEntryParts(entry) {
  if (typeof entry === "string") {
    return { url: entry.trim(), type: null };
  }
  if (entry && typeof entry === "object") {
    const url = String(
      entry.url ?? entry.src ?? entry.href ?? entry.path ?? ""
    ).trim();
    const type =
      entry.type ?? entry.mimeType ?? entry.mediaType ?? entry.kind ?? null;
    return { url, type: typeof type === "string" ? type : null };
  }
  return { url: "", type: null };
}

function isVideoMediaEntry(entry) {
  const { url, type } = mediaEntryParts(entry);
  if (type) {
    const t = type.toLowerCase();
    if (t === "video" || t.startsWith("video/")) return true;
    if (t === "image" || t.startsWith("image/")) return false;
  }
  if (url && VIDEO_URL_RE.test(url)) return true;
  return false;
}

function getFirstProjectImageUrl(imageUrl) {
  if (imageUrl == null) return "";
  const list = Array.isArray(imageUrl) ? imageUrl : [imageUrl];
  for (const item of list) {
    if (isVideoMediaEntry(item)) continue;
    const { url } = mediaEntryParts(item);
    if (url) return url;
  }
  return "";
}

export function getInvestmentThumbUrl(investment) {
  const fromProject = getFirstProjectImageUrl(investment?.project?.imageUrl);
  const fromRoot = getFirstProjectImageUrl(investment?.imageUrl);
  const fromLegacy =
    typeof investment?.image === "string" && investment.image.trim()
      ? getFirstProjectImageUrl(investment.image.trim())
      : "";
  return fromProject || fromRoot || fromLegacy;
}

export function getInvestmentThumbAlt(investment) {
  return (
    investment?.project?.title ??
    investment?.project?.name ??
    investment?.company ??
    investment?.name ??
    "Project"
  );
}
